/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import {
	Avatar,
	Box,
	Button,
	Center,
	Container,
	Flex,
	HStack,
	Radio,
	RadioGroup,
	SimpleGrid,
	Text,
	useDisclosure,
	useToast,
	VStack,
	AlertDialog,
	AlertDialogOverlay,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogBody,
	AlertDialogFooter,
} from '@chakra-ui/react';
import { faPlusSquare, faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import 'moment/locale/en-gb';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import BreadcrumbComponent from '../../components/BreadcrumbComponent/BreadcrumbComponent';
import CompanySubmissionProposalFilter from '../../components/Filter/CompanySubmissionProposalFilter';
import SelectUserModal from '../../components/Modal/SelectUserModal';
import c from '../../constant';
import useStore from '../../store';
import datePickerFromToToDate from '../../utils/datePickerFromToToDate';

const Q_USER_BY_ID = gql`
	query get($id: String!) {
		getUserById(id: $id) {
			id
			fullName
			fullNameKhmer
			avatar
		}
	}
`;

const Q_USER_PROPOSALS = gql`
	query get(
		$showIncomplete: Boolean
		$spam: Boolean
		$showNearlyDue: Boolean
		$read: Boolean
		$publicService: Boolean
		$toDate: DateTime
		$fromDate: DateTime
		$office: String
		$department: String
		$subject: String
		$companyName: String
		$no: Float
		$userId: String!
	) {
		getCompanySubmissionProposalsByUserId(
			showIncomplete: $showIncomplete
			spam: $spam
			showNearlyDue: $showNearlyDue
			read: $read
			publicService: $publicService
			toDate: $toDate
			fromDate: $fromDate
			office: $office
			department: $department
			subject: $subject
			companyName: $companyName
			no: $no
			userId: $userId
		) {
			id
			no
		}
	}
`;

const Q_PROPOSALS_USER_UNASSIGNED = gql`
	query get($userId: String!, $proposalIds: [String!]!) {
		getCompanySubmissionProposalsUserNotAssigned(userId: $userId, proposalIds: $proposalIds)
	}
`;

const M_ASSIGN_USER_PROPOSALS = gql`
	mutation create($userId: String!, $proposalIds: [String!]!, $notification: Boolean!) {
		assignCompanySubmissionProposalsToUser(userId: $userId, proposalIds: $proposalIds, notification: $notification)
	}
`;

function TransferProposal() {
	moment.locale('en-gb');
	const history = useHistory();
	const location = useLocation();
	const urlParams = new URLSearchParams(location.search);
	const id = urlParams.get('id') ?? null;
	const companyName = urlParams.get('companyName') ?? null;
	const subject = urlParams.get('subject') ?? null;
	const no = urlParams.get('number') ?? null;
	const department = urlParams.get('department') ?? null;
	const office = urlParams.get('office') ?? null;
	const status = urlParams.get('status') ?? 'all';
	const fromDate = urlParams.get('fromDate') ?? null;
	const toDate = urlParams.get('toDate') ?? null;
	const read = urlParams.get('read') ?? null;
	const spam = urlParams.get('showSpam') ?? null;
	const showNearlyDue = urlParams.get('showNearlyDue') ?? null;
	const publicService = urlParams.get('type') ?? null;
	const showIncomplete = urlParams.get('showIncomplete') ?? null;
	const toast = useToast();
	const userModal = useDisclosure();
	const filterModal = useDisclosure();
	const confirmModal = useDisclosure();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const [transferer, setTransferer] = useState(null);
	const [transferee, setTransferee] = useState(null);
	const [queries, setQueries] = useState(null);
	const [queriesCount, setQueriesCount] = useState(null);
	const [notification, setNotification] = useState('none');
	const [fetchTransferer, { data }] = useLazyQuery(Q_USER_BY_ID);
	const [fetchTransfererProposals, { data: dataTransfererProposal }] = useLazyQuery(Q_USER_PROPOSALS);
	const [fetchProposalsUserUnAssigned, { data: dataProposalsUserUnAssigned }] = useLazyQuery(Q_PROPOSALS_USER_UNASSIGNED);
	const [assignProposalsToUser, { loading }] = useMutation(M_ASSIGN_USER_PROPOSALS);

	useEffect(loadTransferer, [id]);

	useEffect(() => setTransferer(data?.getUserById), [data]);

	useEffect(
		() => setQueries({ no, companyName, subject, department, office, fromDate, toDate, read, publicService, spam, showNearlyDue, showIncomplete }),
		[no, companyName, subject, department, office, fromDate, toDate, read, publicService, spam, showNearlyDue, showIncomplete],
	);

	useEffect(() => {
		if (queries) {
			const values = Object?.values(queries)?.filter(Boolean);
			setQueriesCount(values?.length > 0 ? `(${values?.length})` : null);
		}
	}, [queries]);

	useEffect(loadTransfererProposals, [
		transferer,
		no,
		companyName,
		subject,
		department,
		office,
		fromDate,
		toDate,
		read,
		publicService,
		spam,
		showNearlyDue,
		showIncomplete,
	]);

	useEffect(loadProposalsUserUnassigned, [dataTransfererProposal?.getCompanySubmissionProposalsByUserId, transferee]);

	function loadTransferer() {
		if (id) {
			fetchTransferer({ variables: { id } });
		}
	}

	function loadTransfererProposals() {
		if (transferer) {
			const variables = {
				showIncomplete: showIncomplete === 'true' ? true : null,
				spam: spam === 'true' ? true : null,
				showNearlyDue: showNearlyDue === 'true' ? true : null,
				read: read === 'false' ? false : null,
				publicService: publicService === 'PUBLIC_SERVICE' ? true : null,
				toDate: datePickerFromToToDate(toDate),
				fromDate: datePickerFromToToDate(fromDate),
				office,
				department,
				subject,
				companyName,
				no: parseInt(no),
				userId: transferer?.id,
			};
			fetchTransfererProposals({ variables });
		}
	}

	function loadProposalsUserUnassigned() {
		if (transferee) {
			const userId = transferee?.id;
			const proposalIds = dataTransfererProposal?.getCompanySubmissionProposalsByUserId?.map((p) => p?.id) ?? [];
			const variables = { userId, proposalIds };
			fetchProposalsUserUnAssigned({ variables });
		}
	}

	function handleTransferProposals() {
		if (dataTransfererProposal?.getCompanySubmissionProposalsByUserId?.length <= 0) {
			toast({
				title: lang === 'kh' ? 'មិនមានការស្នើសុំដើម្បីផ្ទេរ' : 'There is no proposal to transfer',
				status: 'warning',
				isClosable: true,
				position: 'bottom',
			});
			return;
		}
		if (!transferee) {
			toast({
				title: lang === 'kh' ? 'មិនមានអ្នកទទួលការស្នើសុំ' : 'There is no transferee',
				status: 'warning',
				isClosable: true,
				position: 'bottom',
			});
			return;
		}
		if (dataProposalsUserUnAssigned?.getCompanySubmissionProposalsUserNotAssigned?.length <= 0) {
			toast({
				title: lang === 'kh' ? 'មិនមានការស្នើសុំដើម្បីផ្ទេរ' : 'There is no proposal to transfer',
				status: 'warning',
				isClosable: true,
				position: 'bottom',
			});
			return;
		}
		confirmModal?.onOpen();
	}

	function UserAvatar({ userInfo = null }) {
		return (
			<>
				{userInfo && (
					<Flex w="150px" justifyContent="center" alignItems="center" direction="column">
						<Avatar
							name={lang === 'kh' ? (userInfo?.fullNameKhmer ?? userInfo?.username) : (userInfo?.fullName ?? userInfo?.username)}
							src={userInfo?.avatar?.icon ? c.baseFilePath + userInfo?.avatar?.icon : null}
						/>
						<Text fontWeight="bold">
							{lang === 'kh' ? (userInfo?.fullNameKhmer ?? userInfo?.username) : (userInfo?.fullName ?? userInfo?.username)}
						</Text>
					</Flex>
				)}
			</>
		);
	}

	return (
		<>
			<Container maxW="container.xl" mt="16px" mb="16px">
				<BreadcrumbComponent
					list={[
						{
							name: t('Users'),
							path: '/users?page=1',
						},
						{
							name: t('Transfer Proposal'),
							path: '#',
						},
					]}
				/>
				<Center mt="5">
					<Box
						w="100%"
						bg="white"
						borderRadius={c.borderRadius}
						border="1px solid #dbdbdb"
						boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
						pb="32px"
					>
						<Flex bg="#FAFAFA" p="16px" pt="8px" pb="8px" mb="16px" justifyContent="space-between" borderBottom="1px solid #dbdbdb">
							<Text fontSize="x-large" fontWeight="bold">
								{t('Transfer Proposal')}
							</Text>
						</Flex>
						<Box p="16px">
							<SimpleGrid columns={[1, 1, 2]} spacing={12}>
								<Flex direction="column" justifyContent="center" alignItems="center">
									{transferer && (
										<VStack spacing={6}>
											<VStack spacing={4}>
												<UserAvatar userInfo={transferer} />
												<Button
													onClick={filterModal?.onOpen}
													leftIcon={<FontAwesomeIcon icon={faSlidersH} style={{ fontSize: 18 }} />}
													variant="outline"
													colorScheme="blue"
													_focus={{ boxShadow: 'none' }}
												>
													{t('Filter')}&nbsp;{queriesCount}
												</Button>
											</VStack>
											{dataTransfererProposal?.getCompanySubmissionProposalsByUserId && (
												<VStack spacing={2}>
													<Text fontSize="4xl" fontWeight="bold" color="green.500">
														{`${dataTransfererProposal?.getCompanySubmissionProposalsByUserId?.length}`}
													</Text>
												</VStack>
											)}
											<SimpleGrid columns={[5, 8]} spacing={2}>
												{dataTransfererProposal?.getCompanySubmissionProposalsByUserId?.map((proposal) => (
													<Text fontSize="xl" fontWeight="semibold" color="green.500">
														{proposal?.no}
													</Text>
												))}
											</SimpleGrid>
											{dataTransfererProposal?.getCompanySubmissionProposalsByUserId && (
												<VStack spacing={4}>
													<RadioGroup onChange={setNotification} value={notification}>
														<HStack>
															<Text fontSize="md">{t('Notification')}</Text>
															<Radio value="none">{t('None')}</Radio>
															<Radio value="all">{t('All')}</Radio>
														</HStack>
													</RadioGroup>
													<Button onClick={handleTransferProposals} mt={1} colorScheme="green" variant="solid">
														{t('Transfer Proposal')}
													</Button>
												</VStack>
											)}
										</VStack>
									)}
								</Flex>
								<Flex direction="column" alignItems="center">
									<VStack spacing={4}>
										<Button
											onClick={userModal?.onOpen}
											leftIcon={<FontAwesomeIcon icon={faPlusSquare} style={{ fontSize: 18 }} />}
											colorScheme="blue"
											variant="outline"
										>
											{transferee ? t('Change Transferee') : t('Choose Transferee')}
										</Button>
										{transferee && <UserAvatar userInfo={transferee} />}
										{transferee &&
											dataProposalsUserUnAssigned?.getCompanySubmissionProposalsUserNotAssigned?.length >= 0 &&
											dataTransfererProposal?.getCompanySubmissionProposalsByUserId?.length >= 0 && (
												<VStack spacing={6}>
													<Text fontSize="4xl" fontWeight="bold" color="orange.500">
														{`${dataProposalsUserUnAssigned?.getCompanySubmissionProposalsUserNotAssigned?.length}/${dataTransfererProposal?.getCompanySubmissionProposalsByUserId?.length}`}
													</Text>
													<SimpleGrid columns={[5, 8]} spacing={2}>
														{dataTransfererProposal?.getCompanySubmissionProposalsByUserId?.map((proposal) => {
															if (
																dataProposalsUserUnAssigned?.getCompanySubmissionProposalsUserNotAssigned?.includes(
																	proposal?.id,
																)
															) {
																return (
																	<Text fontSize="xl" fontWeight="semibold" color="orange.500">
																		{proposal?.no}
																	</Text>
																);
															} else {
																return (
																	<Text fontSize="xl" fontWeight="semibold" color="gray.400" textDecoration="line-through">
																		{proposal?.no}
																	</Text>
																);
															}
														})}
													</SimpleGrid>
												</VStack>
											)}
									</VStack>
								</Flex>
							</SimpleGrid>
						</Box>
					</Box>
				</Center>
			</Container>
			<SelectUserModal role="companySubmissionProposal" callback={setTransferee} isOpen={userModal?.isOpen} onClose={userModal?.onClose} />
			<CompanySubmissionProposalFilter
				callback={(e) => history.push(`/users/transfer-proposal${e}&id=${id}`)}
				isOpen={filterModal?.isOpen}
				onClose={filterModal?.onClose}
				fromDate={fromDate}
				toDate={toDate}
				read={read}
				number={no}
				companyName={companyName}
				subject={subject}
				department={department}
				office={office}
				type={publicService}
				status={status}
				showSpam={spam === 'true' ? true : false}
				showNearlyDue={showNearlyDue === 'true' ? true : false}
				showIncomplete={showIncomplete === 'true' ? true : false}
				auditServiceOnly={false}
			/>
			<AlertDialog
				isOpen={confirmModal?.isOpen}
				onClose={confirmModal?.onClose}
				closeOnEsc={loading ? false : true}
				closeOnOverlayClick={loading ? false : true}
			>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('Transfer Proposal')}
						</AlertDialogHeader>
						<AlertDialogBody>{t("Are you sure? You can't undo this action afterwards")}</AlertDialogBody>
						<AlertDialogFooter>
							<Button
								isLoading={loading}
								onClick={() => {
									confirmModal?.onClose();
								}}
							>
								{t('Cancel')}
							</Button>
							<Button
								ml={2}
								colorScheme="green"
								isLoading={loading}
								onClick={() => {
									const variables = {
										userId: transferee?.id,
										proposalIds: dataProposalsUserUnAssigned?.getCompanySubmissionProposalsUserNotAssigned,
										notification: notification === 'all' ? true : false,
									};
									assignProposalsToUser({ variables })
										.then((result) => {
											loadProposalsUserUnassigned();
											confirmModal?.onClose();
											toast({
												title: lang === 'kh' ? 'ការស្នើសុំត្រូវបានផ្ទេរដោយជោគជ័យ' : 'Proposals are transfered successfully',
												status: 'success',
												isClosable: true,
												position: 'bottom-right',
											});
										})
										.catch((error) => {
											toast({
												title: t(error?.message ? error?.message : 'something went wrong'),
												status: 'error',
												isClosable: true,
												position: 'bottom-right',
											});
										});
								}}
							>
								{t('Transfer')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</>
	);
}

export default TransferProposal;
